import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';

import { debounceTime, distinctUntilChanged, filter, map, switchMap } from 'rxjs';

import { LISTING_REPOSITORY } from '@core/data/listing/listing.repository';
import { SearchSuggestion } from '@core/models/listing.state';
import { CartService } from '@core/services/cart.service';
import { ShopperLocationService } from '@core/services/shopper-location.service';
import { ShopperSessionService } from '@core/services/shopper-session.service';
import { TenantScopeService } from '@core/services/tenant-scope.service';

import { CategorySubnavComponent } from '@shared/components/category-subnav/category-subnav.component';
import { IconComponent } from '@shared/components/icon/icon.component';

const DEFAULT_SEARCH_PLACEHOLDER = 'Search one-of-a-kind finds - "mid-century dresser", "Levi\'s"…';

/**
 * Storefront header: logo, search with suggestions, "Deliver to" location,
 * the cart badge and the shopper account affordance. Favorites lives in the
 * account rail rather than here, so the row stays to one commerce action. Swaps branding
 * and search targeting when a tenant storefront scope is active (HLD SM-02);
 * the location button hides in tenant scope - the tenant band explains the
 * context there.
 */
@Component({
  selector: 'app-marketplace-header',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    MatAutocompleteModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatMenuModule,
    IconComponent,
    CategorySubnavComponent
  ],
  templateUrl: './marketplace-header.component.html',
  styleUrl: './marketplace-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MarketplaceHeaderComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly dialog = inject(MatDialog);
  private readonly listings = inject(LISTING_REPOSITORY);

  protected readonly session = inject(ShopperSessionService);
  protected readonly tenantScope = inject(TenantScopeService);
  protected readonly cart = inject(CartService);
  protected readonly shopperLocation = inject(ShopperLocationService);

  protected readonly searchControl = new FormControl('', { nonNullable: true });

  /** Suggestions paired with the query they answered, so the no-match row never lags the input. */
  private readonly suggestionState = toSignal(
    this.searchControl.valueChanges.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      switchMap(q => this.listings.suggest(q).pipe(map(items => ({ q: q.trim(), items }))))
    ),
    { initialValue: { q: '', items: [] as SearchSuggestion[] } }
  );

  protected readonly suggestions = computed(() => this.suggestionState().items);

  /** Set when a 2+ character query matched nothing - rendered as an honest "search everything" row. */
  protected readonly noMatchQuery = computed(() => {
    const { q, items } = this.suggestionState();
    return items.length === 0 && q.length >= 2 ? q : null;
  });

  /** Current router URL, kept fresh for the Sign In returnUrl query param. */
  protected readonly returnUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  protected readonly searchPlaceholder = computed(
    () => this.tenantScope.tenant()?.theme.searchPlaceholder ?? DEFAULT_SEARCH_PLACEHOLDER
  );

  protected readonly locationAriaLabel = computed(() => {
    const chosen = this.shopperLocation.chosenLocation();
    return chosen === null ? 'Set your location' : `Deliver to ${chosen.label}, change location`;
  });

  protected readonly avatarInitial = computed(() => this.session.shopper()?.firstName.charAt(0).toUpperCase() ?? '');

  /** The visible name is aria-hidden, so the trigger names itself in full. */
  protected readonly accountAriaLabel = computed(() => {
    const name = this.session.shopper()?.firstName;
    return name ? `Account menu for ${name}` : 'Account menu';
  });

  /** Badge links speak their counts, e.g. "Cart, 3 items" (F2 a11y). */
  protected readonly cartAriaLabel = computed(() => {
    const count = this.cart.lineCount();
    return count === 0 ? 'Cart' : `Cart, ${count} ${count === 1 ? 'item' : 'items'}`;
  });

  constructor() {
    // Keep the search box in step with the URL's `q` param so landing on (or
    // returning to) /search?q=… shows the query being refined, not an empty box.
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe(params => {
      const q = params.get('q') ?? '';
      if (q !== this.searchControl.value) this.searchControl.setValue(q, { emitEvent: false });
    });
  }

  protected onSearchSubmit(): void {
    const q = this.searchControl.value.trim();
    // An emptied box submitted on a results page clears the query; elsewhere
    // it opens the full catalog - the button always does something.
    this.navigateToSearch(q ? { q } : { q: null });
  }

  protected onSuggestionSelected(event: MatAutocompleteSelectedEvent): void {
    const label = event.option.value as string;
    const suggestion = this.suggestions().find(s => s.label === label);
    if (suggestion && suggestion.categorySlug !== null) {
      this.searchControl.setValue('', { emitEvent: false });
      this.navigateToSearch({ category: suggestion.categorySlug, q: null });
      return;
    }
    this.navigateToSearch({ q: label });
  }

  protected openLocationDialog(): void {
    // Lazy import keeps MatDialog's select/dialog machinery out of the
    // initial bundle - the dialog loads on first open.
    void import('@shared/components/location-dialog/location-dialog.component').then(m =>
      this.dialog.open(m.LocationDialogComponent, { width: '400px' })
    );
  }

  protected signOut(): void {
    this.session.signOut();
    void this.router.navigate(['/']);
  }

  private navigateToSearch(params: Record<string, string | null>): void {
    const tenant = this.tenantScope.tenant();
    const queryParams: Record<string, string | null> = tenant ? { ...params, tenant: tenant.slug } : { ...params };
    const path = this.router.url.split('?')[0];
    // Already on a results page: merge so active filters and sort survive the
    // new query; a changed query always restarts at page 1.
    if (path === '/search' || path.endsWith('/search')) {
      void this.router.navigate(['/search'], {
        queryParams: { ...queryParams, page: null },
        queryParamsHandling: 'merge'
      });
      return;
    }
    const cleaned = Object.fromEntries(Object.entries(queryParams).filter(([, value]) => value !== null));
    void this.router.navigate(['/search'], { queryParams: cleaned });
  }
}
